'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, DollarSign, ShoppingCart, Clock, BarChart3, Award,  } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,  } from 'recharts';

import Icon from '@/components/ui/AppIcon';


// ─── Analytics Data ───────────────────────────────────────────────────────────
const MONTHLY_SPEND = [
  { month: 'Jan', spend: 12400000, orders: 5, savings: 800000 },
  { month: 'Feb', spend: 15200000, orders: 7, savings: 1200000 },
  { month: 'Mar', spend: 18900000, orders: 9, savings: 1500000 },
  { month: 'Apr', spend: 18500000, orders: 8, savings: 900000 },
  { month: 'May', spend: 22300000, orders: 11, savings: 1800000 },
  { month: 'Jun', spend: 19800000, orders: 9, savings: 1100000 },
  { month: 'Jul', spend: 31200000, orders: 14, savings: 2400000 },
  { month: 'Aug', spend: 27600000, orders: 12, savings: 2100000 },
  { month: 'Sep', spend: 31107500, orders: 4, savings: 500000 },
];

const SPEND_BY_CATEGORY = [
  { name: 'Feed', value: 89500000, color: '#f59e0b' },
  { name: 'Vaccines & Meds', value: 18200000, color: '#10b981' },
  { name: 'PPE & Safety', value: 7800000, color: '#3b82f6' },
  { name: 'Chemicals', value: 6400000, color: '#8b5cf6' },
  { name: 'Equipment', value: 12300000, color: '#f97316' },
  { name: 'Other', value: 4800000, color: '#6b7280' },
];

const SUPPLIER_PERFORMANCE = [
  { supplier: 'Ugachick', score: 92, deliveryRate: 95, qualityRate: 98, spend: 89500000 },
  { supplier: 'KVS', score: 96, deliveryRate: 100, qualityRate: 100, spend: 18200000 },
  { supplier: 'EA Agri-Inputs', score: 78, deliveryRate: 82, qualityRate: 88, spend: 6400000 },
  { supplier: 'Nile Agro', score: 85, deliveryRate: 90, qualityRate: 92, spend: 12300000 },
  { supplier: 'Jinja Supplies', score: 81, deliveryRate: 85, qualityRate: 87, spend: 4800000 },
];

const CYCLE_TIME = [
  { stage: 'Requisition', days: 1.2 },
  { stage: 'Approval', days: 2.4 },
  { stage: 'RFQ', days: 3.8 },
  { stage: 'PO Issue', days: 1.5 },
  { stage: 'Delivery', days: 5.2 },
  { stage: 'GRN', days: 0.8 },
];

const PO_STATUS_DIST = [
  { name: 'Fully Delivered', value: 28, color: '#10b981' },
  { name: 'Partially Delivered', value: 8, color: '#f97316' },
  { name: 'Approved/Issued', value: 12, color: '#3b82f6' },
  { name: 'Pending Approval', value: 5, color: '#f59e0b' },
  { name: 'Cancelled', value: 3, color: '#ef4444' },
];

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function AnalyticsKpi({ title, value, sub, icon: Icon, color }: { title: string; value: string; sub: string; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
      <div className="text-xs font-semibold text-gray-500 mt-0.5">{title}</div>
      <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
    </div>
  );
}

export default function ProcurementAnalyticsContent() {
  const [period, setPeriod] = useState('ytd');

  const totalSpend = MONTHLY_SPEND.reduce((s, m) => s + m.spend, 0);
  const totalSavings = MONTHLY_SPEND.reduce((s, m) => s + m.savings, 0);
  const totalOrders = MONTHLY_SPEND.reduce((s, m) => s + m.orders, 0);
  const avgCycleTime = CYCLE_TIME.reduce((s, c) => s + c.days, 0);

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Procurement Analytics</h1>
          <p className="text-xs text-gray-400 mt-0.5">Spend analysis, supplier performance, and procurement KPIs</p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {[{ id: 'ytd', label: 'YTD' }, { id: 'q3', label: 'Q3 2026' }, { id: 'sep', label: 'Sep 2026' }].map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${period === p.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <AnalyticsKpi title="Total Spend" value={`UGX ${(totalSpend / 1000000).toFixed(1)}M`} sub="Jan–Sep 2026" icon={DollarSign} color="bg-orange-500" />
        <AnalyticsKpi title="Purchase Orders" value={totalOrders.toString()} sub="Total issued" icon={ShoppingCart} color="bg-indigo-500" />
        <AnalyticsKpi title="Avg PO Value" value={`UGX ${((totalSpend / totalOrders) / 1000000).toFixed(1)}M`} sub="Per order" icon={BarChart3} color="bg-blue-500" />
        <AnalyticsKpi title="Total Savings" value={`UGX ${(totalSavings / 1000000).toFixed(1)}M`} sub="From quotation comparison" icon={Award} color="bg-green-600" />
        <AnalyticsKpi title="Avg Cycle Time" value={`${avgCycleTime.toFixed(1)} days`} sub="Req to GRN" icon={Clock} color="bg-purple-500" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

        {/* Monthly Spend */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Monthly Procurement Spend</h3>
          <p className="text-xs text-gray-400 mb-4">Spend trend with savings — Jan to Sep 2026</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MONTHLY_SPEND}>
              <defs>
                <linearGradient id="spendGrad2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v: number, name: string) => [`UGX ${v.toLocaleString()}`, name === 'spend' ? 'Spend' : 'Savings']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend formatter={v => v === 'spend' ? 'Spend' : 'Savings'} />
              <Area type="monotone" dataKey="spend" stroke="#f97316" strokeWidth={2} fill="url(#spendGrad2)" />
              <Area type="monotone" dataKey="savings" stroke="#10b981" strokeWidth={2} fill="url(#savingsGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Spend by Category */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Spend by Category</h3>
          <p className="text-xs text-gray-400 mb-3">YTD 2026</p>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={SPEND_BY_CATEGORY} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                {SPEND_BY_CATEGORY.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [`UGX ${v.toLocaleString()}`, '']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {SPEND_BY_CATEGORY.map(cat => (
              <div key={cat.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-gray-600">{cat.name}</span>
                </div>
                <span className="font-semibold text-gray-800">{(cat.value / 1000000).toFixed(1)}M</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

        {/* Supplier Performance */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Supplier Performance Scores</h3>
          <div className="space-y-3">
            {SUPPLIER_PERFORMANCE.map(sup => (
              <div key={sup.supplier}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-700">{sup.supplier}</span>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Delivery: <strong className="text-gray-700">{sup.deliveryRate}%</strong></span>
                    <span>Quality: <strong className="text-gray-700">{sup.qualityRate}%</strong></span>
                    <span className={`font-bold text-sm ${sup.score >= 90 ? 'text-green-600' : sup.score >= 80 ? 'text-amber-600' : 'text-red-500'}`}>{sup.score}</span>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${sup.score >= 90 ? 'bg-green-500' : sup.score >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${sup.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Procurement Cycle Time */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Procurement Cycle Time</h3>
          <p className="text-xs text-gray-400 mb-4">Average days per stage — YTD 2026</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={CYCLE_TIME} layout="vertical" margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit=" days" />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={80} />
              <Tooltip formatter={(v: number) => [`${v} days`, 'Avg Time']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="days" fill="#6366f1" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* PO Status + Monthly Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">PO Status Distribution</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={PO_STATUS_DIST} cx="50%" cy="50%" outerRadius={65} paddingAngle={2} dataKey="value">
                {PO_STATUS_DIST.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v} orders`, '']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {PO_STATUS_DIST.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-gray-600">{s.name}</span>
                </div>
                <span className="font-semibold text-gray-800">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Monthly Purchase Orders</h3>
          <p className="text-xs text-gray-400 mb-4">Number of POs issued per month</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={MONTHLY_SPEND}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: number) => [`${v} orders`, 'POs']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="orders" fill="#f97316" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
}
